import { useEffect, useState } from 'react';
import { Modal, BlockStack, InlineStack, Text, Checkbox, Banner, Button, Spinner, Box, Divider } from '@shopify/polaris';
import { getRequest } from '../../../shared/components/functions.jsx';

const PART_DOCUMENT_TYPE_ID = 25;
const formatDate = (value) => (value ? String(value).substring(0, 10) : '');

/*
  Duplicates a saved product into a new one (e.g. a new version of a part): product data, stock locations
  (quantity 0) and bill of materials. The user chooses whether the current product becomes discontinued and
  which of its documents (PDF, DWG, STEP, ...) are copied along. The server copies the files before it answers.
*/
export default function DuplicateProductModal({ open, onClose, productId, productName, isDiscontinued, onDuplicated }) {
  const [loading, setLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [selectedDocumentIds, setSelectedDocumentIds] = useState([]);
  const [discontinueSource, setDiscontinueSource] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [result, setResult] = useState(null); // kept when some files could not be copied, so the user sees which

  useEffect(() => {
    if (!open || !productId || productId === 'new') return undefined;
    let cancelled = false;

    const loadDocuments = async () => {
      setLoading(true);
      setErrorMessage('');
      setResult(null);
      setDiscontinueSource(false);
      const data = await getRequest(`/api/documents/id/typeid?id=${productId}&typeID=${PART_DOCUMENT_TYPE_ID}`);
      if (cancelled) return;
      const list = Array.isArray(data) ? data : [];
      setDocuments(list);
      setSelectedDocumentIds(list.map((doc) => doc.DocumentID));
      setLoading(false);
    };
    loadDocuments();

    return () => {
      cancelled = true;
    };
  }, [open, productId]);

  const toggleDocument = (documentId, checked) => setSelectedDocumentIds((ids) => (checked ? [...ids, documentId] : ids.filter((id) => id !== documentId)));
  const allSelected = documents.length > 0 && selectedDocumentIds.length === documents.length;

  const handleDuplicate = async () => {
    setSubmitting(true);
    setErrorMessage('');
    try {
      const response = await fetch(`/api/products/${productId}/duplicate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ discontinueSource, documentIds: selectedDocumentIds })
      });
      const data = await response.json().catch(() => null);
      if (!response.ok) throw new Error(data?.msg || 'The product could not be duplicated.');

      if (data.documents?.failed?.length) {
        setResult(data);
      } else {
        onDuplicated(data);
      }
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderResult = () => (
    <BlockStack gap="300">
      <Banner tone="warning" title={`Product #${result.product.ProductID} was created, but not every file could be copied`}>
        <BlockStack gap="100">
          {result.documents.failed.map((failure) => (
            <Text as="p" key={failure.documentId}>
              {failure.filename || `Document ${failure.documentId}`}: {failure.reason}
            </Text>
          ))}
        </BlockStack>
      </Banner>
      <Text as="p">{result.documents.copied.length} file(s) copied. Missing files can be uploaded on the new product.</Text>
    </BlockStack>
  );

  const renderForm = () => (
    <BlockStack gap="400">
      <Text as="p">
        Creates a new product with the same data, stock locations (quantity 0), bill of materials and technical description as <strong>{productName || `#${productId}`}</strong>. Stock, price history
        and sales stay with this product.
      </Text>
      <Checkbox
        label="Mark this product as discontinued"
        helpText={isDiscontinued ? 'This product is already discontinued.' : 'Use this when the new product replaces this one.'}
        checked={discontinueSource || !!isDiscontinued}
        disabled={!!isDiscontinued || submitting}
        onChange={setDiscontinueSource}
      />
      <Divider />
      <BlockStack gap="200">
        <InlineStack align="space-between" blockAlign="center">
          <Text as="h3" variant="headingSm">
            Copy documents to the new product?
          </Text>
          {documents.length > 1 ? (
            <Button variant="plain" onClick={() => setSelectedDocumentIds(allSelected ? [] : documents.map((doc) => doc.DocumentID))} disabled={submitting}>
              {allSelected ? 'Select none' : 'Select all'}
            </Button>
          ) : null}
        </InlineStack>
        {loading ? (
          <InlineStack gap="200" blockAlign="center">
            <Spinner size="small" />
            <Text as="span" tone="subdued">
              Loading documents...
            </Text>
          </InlineStack>
        ) : documents.length === 0 ? (
          <Text as="p" tone="subdued">
            This product has no documents.
          </Text>
        ) : (
          <BlockStack gap="100">
            {documents.map((doc) => (
              <Checkbox
                key={doc.DocumentID}
                label={doc.Filename}
                helpText={[formatDate(doc.Datum), doc.Omschrijving].filter(Boolean).join(' · ') || undefined}
                checked={selectedDocumentIds.includes(doc.DocumentID)}
                disabled={submitting}
                onChange={(checked) => toggleDocument(doc.DocumentID, checked)}
              />
            ))}
            <Text as="p" tone="subdued" variant="bodySm">
              {selectedDocumentIds.length} of {documents.length} file(s) will be copied.
            </Text>
          </BlockStack>
        )}
      </BlockStack>
      {submitting && selectedDocumentIds.length > 0 ? (
        <InlineStack gap="200" blockAlign="center">
          <Spinner size="small" />
          <Text as="span" tone="subdued">
            Copying {selectedDocumentIds.length} file(s), this can take a moment...
          </Text>
        </InlineStack>
      ) : null}
      {errorMessage ? <Banner tone="critical">{errorMessage}</Banner> : null}
    </BlockStack>
  );

  return (
    <Modal
      open={open}
      onClose={submitting ? () => {} : onClose}
      title="Duplicate product"
      primaryAction={
        result
          ? { content: `Open product #${result.product.ProductID}`, onAction: () => onDuplicated(result) }
          : { content: 'Duplicate', onAction: handleDuplicate, loading: submitting, disabled: loading }
      }
      secondaryActions={result ? [] : [{ content: 'Cancel', onAction: onClose, disabled: submitting }]}
    >
      <Modal.Section>
        <Box>{result ? renderResult() : renderForm()}</Box>
      </Modal.Section>
    </Modal>
  );
}
